import {
  AmPaxAmount,
  AmPaxAmountTotals,
  AmPaxType,
  AmRooms,
  MAX_AM_PAX_AMOUNT,
  MAX_PAX_AMOUNT,
  PaxAmount,
  PaxAmountKey,
  PaxSelectionOption,
} from '../../common';

export const canAddPaxType = (type: PaxAmountKey, paxAmount: PaxAmount): boolean => {
  const { adults, children, c15s, infants } = paxAmount;
  return type === 'infants' ? infants < adults : adults + children + c15s < MAX_PAX_AMOUNT;
};

export const getPaxSelectionOptions = (paxAmount: PaxAmount): PaxSelectionOption[] =>
  Object.keys(paxAmount)
    .sort((a: PaxAmountKey, b: PaxAmountKey) => toSortId(a) - toSortId(b))
    .map((type: PaxAmountKey) => ({
      type,
      label: type,
      amount: paxAmount[type],
      canAdd: canAddPaxType(type, paxAmount),
      canRemove: type === 'adults' ? paxAmount[type] > 1 : paxAmount[type] > 0,
    }));

function toSortId(type: PaxAmountKey) {
  switch (type) {
    case 'adults':
      return 1;
    case 'c15s':
      return 2;
    case 'children':
      return 3;
    case 'infants':
      return 4;
    default:
      return 5;
  }
}

/**
 * @param from inclusive
 * @param to exclusive
 */
export const range = (from: number, to: number): number[] =>
  Array(to - from)
    .fill(from)
    .map((v, i) => v + i);

export const canAddRemoveAmPaxAmount = (
  type: AmPaxType,
  amPaxAmount: AmPaxAmount,
  maxRemainingPaxes: { [key in AmPaxType]: number }
): { canAdd: boolean; canRemove: boolean } => {
  const amount: number = amPaxAmount[type];
  const { adults, children } = amPaxAmount;
  const totalPaxes = adults + children;
  const lessThanMaxTotal = totalPaxes < MAX_AM_PAX_AMOUNT.maxTotalPax;
  const lessThanMaxType = maxRemainingPaxes[type] > 0;
  const lessThanMaxTypePerRoom = amount < MAX_AM_PAX_AMOUNT[type].maxPerRoom;

  return {
    canAdd: lessThanMaxTotal && lessThanMaxType && lessThanMaxTypePerRoom,
    canRemove: amount > MAX_AM_PAX_AMOUNT[type].minPerRoom,
  };
};

export const calculateTotalAndRemainingAmPaxAmount = (rooms: AmRooms): AmPaxAmountTotals => {
  const initial = {
    total: { adults: 0, children: 0 },
    remaining: { adults: MAX_AM_PAX_AMOUNT.adults.maxTotal, children: MAX_AM_PAX_AMOUNT.children.maxTotal },
  };

  return (
    rooms?.reduce((current, { adults, children }) => {
      const total = {
        adults: current.total.adults + adults,
        children: current.total.children + children,
      };
      const remainingTotal = MAX_AM_PAX_AMOUNT.maxTotalPax - (total.adults + total.children);
      const remainingAdults = current.remaining.adults - adults;
      const remainingChildren = current.remaining.children - children;
      const remaining = {
        adults: remainingAdults <= remainingTotal ? remainingAdults : remainingTotal,
        children: remainingChildren <= remainingTotal ? remainingChildren : remainingTotal,
      };

      return {
        total,
        remaining,
      };
    }, initial) ?? initial
  );
};
